import { html, type TemplateResult } from 'lit';
import { MarkdownDoc } from '../../data/markdownEngine';
import { iconPixelArrowLeft, iconPixelEye, iconPixelEyeOff } from '../../icons';
import '../app-button/app-button';

export interface PostReaderProps {
  doc: MarkdownDoc | null;
  isUnlocked: boolean;
  passwordInput: string;
  showPassword?: boolean;
  hasError: boolean;
  onBackToList: () => void;
  onPasswordInput: (value: string) => void;
  onToggleShowPassword?: () => void;
  onUnlockSubmit: () => void;
}

export function renderPostReaderPresentation(props: PostReaderProps): TemplateResult {
  const {
    doc,
    isUnlocked,
    passwordInput,
    showPassword = false,
    hasError,
    onBackToList,
    onPasswordInput,
    onToggleShowPassword,
    onUnlockSubmit,
  } = props;

  if (!doc) {
    return html`<p>Document not found.</p>`;
  }

  const isProtected = Boolean(doc.password);
  const requiresPassword = isProtected && !isUnlocked;

  // 1. Password challenge modal overlay
  if (requiresPassword) {
    return html`
      <div class="password-modal-overlay">
        <div class="password-modal-card">
          <div class="modal-top-bar">
            <app-button
              variant="text"
              label="Back"
              @click=${onBackToList}
            >
              <span slot="icon-left" style="margin-right: 0.2rem;">${iconPixelArrowLeft(12)}</span>
            </app-button>
          </div>

          <h2 class="modal-title">${doc.title}</h2>
          <p class="modal-subtitle">Protected Post</p>

          <form
            class="password-form"
            @submit=${(e: Event) => {
              e.preventDefault();
              onUnlockSubmit();
            }}
          >
            <div class="input-row">
              <div class="password-input-wrapper">
                <input
                  type=${showPassword ? 'text' : 'password'}
                  class="password-input"
                  placeholder="Enter password"
                  .value=${passwordInput}
                  @input=${(e: Event) => onPasswordInput((e.target as HTMLInputElement).value)}
                  autofocus
                />
                ${onToggleShowPassword
                  ? html`
                      <button
                        type="button"
                        class="toggle-password-btn"
                        title=${showPassword ? 'Hide password' : 'Show password'}
                        @click=${onToggleShowPassword}
                      >
                        ${showPassword ? iconPixelEyeOff(14) : iconPixelEye(14)}
                      </button>
                    `
                  : ''}
              </div>
              <app-button
                variant="primary"
                label="Unlock"
                @click=${onUnlockSubmit}
              ></app-button>
            </div>
            ${hasError ? html`<div class="error-msg">Incorrect password</div>` : ''}
          </form>
        </div>
      </div>
    `;
  }

  // 2. Unlocked Article Content
  const authorDisplay = Array.isArray(doc.author)
    ? doc.author.join(', ')
    : doc.author || 'Geireann Lindfield Roberts';

  return html`
    <article>
      <app-button
        variant="text"
        label="Back"
        @click=${onBackToList}
      >
        <span slot="icon-left" style="margin-right: 0.2rem;">${iconPixelArrowLeft(12)}</span>
      </app-button>

      <header class="article-header" style="margin-top: 1rem;">
        <h1>${doc.title}</h1>
        <div class="meta-row">
          <span>By ${authorDisplay}</span>
          <span>•</span>
          <span>Date: ${doc.date}</span>
          <span>•</span>
          <span>${doc.readingTime}</span>
          <span>•</span>
          <span class="type-badge">${doc.type}</span>
        </div>

        ${doc.coverImage
          ? html`
              <img
                src=${doc.coverImage}
                alt=${doc.title}
                class="cover-banner-image"
                style="width: 100%; max-height: 380px; object-fit: cover; border-radius: 8px; margin-top: 1.25rem; border: 1px solid var(--border-color, #e5e5de);"
              />
            `
          : ''}
      </header>

      <div class="content-area" .innerHTML=${doc.htmlContent}></div>
    </article>
  `;
}
